import { CSS_PROPERTIES } from '@wix/component-protocol/schema';

import {
  StylesDerivedNativeActionNames,
  StylesDerivedNativeActions,
} from '@wix/editor-platform-host-integration-apis';
import { getEditorType } from '../../../intentInstallService/helpers';
import { EditorType } from '@wix/editor-platform-sdk-types';

type CssPropertyType =
  (typeof CSS_PROPERTIES.CSS_PROPERTY_TYPE)[keyof typeof CSS_PROPERTIES.CSS_PROPERTY_TYPE];

export const BORDER_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.border,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderTop,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderBottom,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderLeft,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderRight,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineStart,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineEnd,
] as const;

export const BORDER_COLOR_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderColor,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderTopColor,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderBottomColor,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderLeftColor,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderRightColor,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineStartColor,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineEndColor,
] as const;

export const BORDER_WIDTH_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderWidth,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderBottomWidth,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderLeftWidth,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderRightWidth,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderTopWidth,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineStartWidth,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineEndWidth,
] as const;

export const BORDER_STYLE_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderStyle,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderBottomStyle,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderLeftStyle,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderRightStyle,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderTopStyle,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineStartStyle,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderInlineEndStyle,
] as const;

export const CORNER_RADIUS_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderTopLeftRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderTopRightRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderBottomLeftRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderBottomRightRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderStartStartRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderStartEndRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderEndStartRadius,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.borderEndEndRadius,
] as const;

export const PADDING_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.padding,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.paddingTop,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.paddingRight,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.paddingBottom,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.paddingLeft,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.paddingInlineStart,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.paddingInlineEnd,
] as const;

export const GAP_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.gap,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.rowGap,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.columnGap,
] as const;

export const SHADOW_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.boxShadow,
] as const;

// Extra typography properties supported in the responsive editors (incl. Studio2)
// as part of the text theme. textDecorationLine has dedicated controls
// (underline / line-through); letterSpacing is carried and propagated by the
// theme without a dedicated control.
const RESPONSIVE_EDITOR_FONT_EXTRA_PROPERTY_TYPES: readonly CssPropertyType[] =
  [
    CSS_PROPERTIES.CSS_PROPERTY_TYPE.textDecorationLine,
    CSS_PROPERTIES.CSS_PROPERTY_TYPE.letterSpacing,
  ];

export const FONT_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.font,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontFamily,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontWeight,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.fontSize,
] as const;

export const RESPONSIVE_FONT_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  ...FONT_CSS_PROPERTY_TYPES,
  ...RESPONSIVE_EDITOR_FONT_EXTRA_PROPERTY_TYPES,
] as const;

export const TEXT_STYLE_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  ...FONT_CSS_PROPERTY_TYPES,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.color,
] as const;

export const STROKE_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.stroke,
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.strokeWidth,
] as const;

export const FILTER_CSS_PROPERTY_TYPES: readonly CssPropertyType[] = [
  CSS_PROPERTIES.CSS_PROPERTY_TYPE.filter,
] as const;

export const SHORTHAND_PROPERTIES_THAT_EXPAND_TO_COLOR: readonly CssPropertyType[] =
  [...BORDER_CSS_PROPERTY_TYPES];

export const ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME: Record<
  StylesDerivedNativeActionNames,
  readonly CssPropertyType[]
> = {
  [StylesDerivedNativeActions.globalColorsStyles]: [
    CSS_PROPERTIES.CSS_PROPERTY_TYPE.color,
    CSS_PROPERTIES.CSS_PROPERTY_TYPE.backgroundColor,
    ...BORDER_COLOR_CSS_PROPERTY_TYPES,
  ],
  [StylesDerivedNativeActions.textStyles]: [...TEXT_STYLE_CSS_PROPERTY_TYPES],
  [StylesDerivedNativeActions.borderStyles]: [
    ...BORDER_CSS_PROPERTY_TYPES,
    ...BORDER_COLOR_CSS_PROPERTY_TYPES,
    ...BORDER_WIDTH_CSS_PROPERTY_TYPES,
    ...BORDER_STYLE_CSS_PROPERTY_TYPES,
  ],
  [StylesDerivedNativeActions.cornerStyles]: [
    ...CORNER_RADIUS_CSS_PROPERTY_TYPES,
  ],
  [StylesDerivedNativeActions.fillStyles]: [
    CSS_PROPERTIES.CSS_PROPERTY_TYPE.backgroundImage,
    CSS_PROPERTIES.CSS_PROPERTY_TYPE.backgroundColor,
    CSS_PROPERTIES.CSS_PROPERTY_TYPE.background,
  ],
  [StylesDerivedNativeActions.shadowStyles]: [...SHADOW_CSS_PROPERTY_TYPES],
  [StylesDerivedNativeActions.spacingStyles]: [
    ...PADDING_CSS_PROPERTY_TYPES,
    ...GAP_CSS_PROPERTY_TYPES,
  ],
  [StylesDerivedNativeActions.strokeStyles]: [...STROKE_CSS_PROPERTY_TYPES],
  [StylesDerivedNativeActions.filterStyles]: [...FILTER_CSS_PROPERTY_TYPES],
} as const;

const checkIsResponsiveEditor = () => getEditorType() === EditorType.Responsive;

export const getFontPropeties = () => {
  return checkIsResponsiveEditor()
    ? RESPONSIVE_FONT_CSS_PROPERTY_TYPES
    : FONT_CSS_PROPERTY_TYPES;
};

export const getAllowedPropertiesForAction = (
  actionName: StylesDerivedNativeActionNames,
): readonly CssPropertyType[] => {
  switch (actionName) {
    case StylesDerivedNativeActions.textStyles:
      const allowedProperties =
        ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME[actionName];

      return checkIsResponsiveEditor()
        ? allowedProperties.concat(RESPONSIVE_EDITOR_FONT_EXTRA_PROPERTY_TYPES)
        : allowedProperties;
    default:
      return ALLOWED_CSS_PROPERTY_TYPES_BY_ACTION_NAME[actionName];
  }
};

export const GLOBAL_DESIGN_INFO_PREFIX = '[GLOBAL_DESIGN] INFO: ';
export const GLOBAL_DESIGN_ERROR_PREFIX = '[GLOBAL_DESIGN] ERROR: ';
